import { doc, DocumentData, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { db } from '../../shared/utils/firebase';
import { asDate } from '../staffPerformance/services/staffPerformanceService';
import { CoachScheduleDoc, CoachSchedulesMeta, DateSyncState, ScheduleSession } from './types';

export const COACH_SCHEDULES_COLLECTION = 'coach_schedules';
export const COACH_SCHEDULES_META_COLLECTION = 'coach_schedules_meta';
const META_DOC_ID = 'latest';
const LOG_PREFIX = '[coach-schedules]';

const objectOr = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
const numberOr0 = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);
const numberOrNull = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);
const stringOrNull = (value: unknown): string | null => (typeof value === 'string' ? value : null);
const triggerOrNull = (value: unknown): DateSyncState['trigger'] => (value === 'scheduled' || value === 'manual' ? value : null);

function mapDateState(value: unknown): DateSyncState {
  const entry = objectOr(value);
  return {
    status: entry.status === 'skipped' ? 'skipped' : 'ok',
    reason: stringOrNull(entry.reason),
    sessions: numberOr0(entry.sessions),
    staff: numberOr0(entry.staff),
    synced_at: asDate(entry.synced_at),
    run_id: stringOrNull(entry.run_id),
    trigger: triggerOrNull(entry.trigger),
  };
}

/** `coach_schedules_meta/latest`. */
export function mapCoachSchedulesMeta(data: DocumentData): CoachSchedulesMeta {
  const dates: Record<string, DateSyncState> = {};
  Object.entries(objectOr(data.dates)).forEach(([date, value]) => {
    dates[date] = mapDateState(value);
  });
  return {
    status: data.status === 'partial' || data.status === 'error' ? data.status : 'ok',
    run_id: stringOrNull(data.run_id) ?? '',
    trigger: triggerOrNull(data.trigger),
    dates,
    written: numberOr0(data.written),
    deleted: numberOr0(data.deleted),
    unchanged: numberOr0(data.unchanged),
    api_calls: numberOr0(data.api_calls),
    error: stringOrNull(data.error),
    computed_at: asDate(data.computed_at),
    last_success_at: asDate(data.last_success_at),
  };
}

function mapSession(value: unknown): ScheduleSession | null {
  const entry = objectOr(value);
  const startLocal = stringOrNull(entry.start_local);
  // Ids arrive as strings from the function; a number is tolerated so a hand-edited doc still shows.
  const clientId = typeof entry.client_id === 'number' ? String(entry.client_id) : stringOrNull(entry.client_id);
  if (!startLocal || !clientId) return null;
  return {
    appointment_id: numberOr0(entry.appointment_id),
    start_local: startLocal,
    end_local: stringOrNull(entry.end_local),
    start_at: asDate(entry.start_at),
    duration_min: numberOrNull(entry.duration_min),
    status: stringOrNull(entry.status) ?? '',
    session_type_id: numberOrNull(entry.session_type_id),
    session_type_name: stringOrNull(entry.session_type_name),
    program_id: numberOrNull(entry.program_id),
    location_id: numberOrNull(entry.location_id),
    location_name: stringOrNull(entry.location_name),
    client_id: clientId,
    client_name: stringOrNull(entry.client_name),
    first_appointment: entry.first_appointment === true,
    staff_requested: entry.staff_requested === true,
  };
}

/** `coach_schedules/{staffId}_{date}`; a session without a start or a client is dropped rather than guessed at. */
export function mapCoachScheduleDoc(data: DocumentData): CoachScheduleDoc {
  const sessions = Array.isArray(data.sessions)
    ? data.sessions.map(mapSession).filter((session: ScheduleSession | null): session is ScheduleSession => session !== null)
    : [];
  return {
    staff_id: stringOrNull(data.staff_id) ?? '',
    staff_name: stringOrNull(data.staff_name),
    home_location: stringOrNull(data.home_location),
    date: stringOrNull(data.date) ?? '',
    session_count: numberOr0(data.session_count),
    sessions,
    content_hash: stringOrNull(data.content_hash) ?? '',
    changed_at: asDate(data.changed_at),
    expire_at: asDate(data.expire_at),
    run_id: stringOrNull(data.run_id) ?? '',
  };
}

export const coachScheduleDocId = (staffId: string, date: string) => `${staffId}_${date}`;

/**
 * One coach's day. `null` means no document: on a synced date that is a day with no sessions.
 * The rules allow a coach to read their own id even when the document is missing.
 *
 * The app keeps a persistent offline cache, so the first snapshot can be the copy from the last
 * visit. `fromCache` says so, and metadata changes are included so the server's confirmation
 * arrives even when it matches the cached copy.
 */
export function subscribeToCoachSchedule(
  staffId: string,
  date: string,
  onData: (schedule: CoachScheduleDoc | null, fromCache: boolean) => void,
  onError: (error: Error) => void
): Unsubscribe {
  return onSnapshot(
    doc(db, COACH_SCHEDULES_COLLECTION, coachScheduleDocId(staffId, date)),
    { includeMetadataChanges: true },
    (snapshot) => onData(snapshot.exists() ? mapCoachScheduleDoc(snapshot.data() ?? {}) : null, snapshot.metadata.fromCache),
    (error) => {
      console.error(`${LOG_PREFIX} schedule subscription failed for ${date}`, error);
      onError(error);
    }
  );
}

/** `coach_schedules_meta/latest`; `null` until the first run has written it. `fromCache` as for a day. */
export function subscribeToCoachSchedulesMeta(
  onData: (meta: CoachSchedulesMeta | null, fromCache: boolean) => void,
  onError: (error: Error) => void
): Unsubscribe {
  return onSnapshot(
    doc(db, COACH_SCHEDULES_META_COLLECTION, META_DOC_ID),
    { includeMetadataChanges: true },
    (snapshot) => onData(snapshot.exists() ? mapCoachSchedulesMeta(snapshot.data() ?? {}) : null, snapshot.metadata.fromCache),
    (error) => {
      console.error(`${LOG_PREFIX} meta subscription failed`, error);
      onError(error);
    }
  );
}
